import * as React from "react";
import { CheckCircle, XCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { DayCloseLabels } from "./types";

export interface DayCloseSuccessStateProps {
  labels: DayCloseLabels;
  onBackToCashier?: () => void;
  onReopen?: () => void;
  showReopen?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export function DayCloseSuccessState({
  labels, onBackToCashier, onReopen, showReopen, children, className,
}: DayCloseSuccessStateProps) {
  return (
    <div className={cn("text-center space-y-4", className)} role="status" aria-live="polite">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-biz-success-subtle">
        <CheckCircle weight="fill" className="w-12 h-12 text-biz-success" />
      </div>
      <div>
        <h2 className="text-xl font-bold font-display text-biz-foreground">
          {labels.steps.submit.successTitle}
        </h2>
        <p className="text-sm text-biz-foreground-muted mt-1">
          {labels.steps.submit.successDescription}
        </p>
      </div>
      {children}
      <div className="flex items-center justify-center gap-2 pt-2">
        {onBackToCashier && (
          <button
            type="button"
            onClick={onBackToCashier}
            className="h-12 px-5 rounded-biz-btn bg-biz-primary text-biz-primary-foreground font-semibold biz-shadow-sm hover:bg-biz-primary-hover biz-focus-ring"
          >
            {labels.page.backToCashier}
          </button>
        )}
        {showReopen && onReopen && (
          <button
            type="button"
            onClick={onReopen}
            className="h-12 px-4 rounded-biz-btn border border-biz-border-strong bg-biz-surface-elevated text-biz-foreground font-semibold flex items-center gap-2 hover:bg-biz-muted biz-focus-ring"
          >
            <ArrowCounterClockwise className="w-4 h-4" />
            {labels.cta.reopen}
          </button>
        )}
      </div>
    </div>
  );
}

export interface DayCloseErrorStateProps {
  message: string;
  labels: DayCloseLabels;
  onRetry?: () => void;
  className?: string;
}

export function DayCloseErrorState({ message, labels, onRetry, className }: DayCloseErrorStateProps) {
  return (
    <div className={cn("text-center space-y-4 p-6", className)} role="alert">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-biz-danger-subtle">
        <XCircle weight="fill" className="w-12 h-12 text-biz-danger" />
      </div>
      <p className="text-base font-semibold text-biz-foreground">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="h-12 px-5 rounded-biz-btn bg-biz-primary text-biz-primary-foreground font-semibold biz-shadow-sm hover:bg-biz-primary-hover biz-focus-ring"
        >
          {labels.cta.retry}
        </button>
      )}
    </div>
  );
}

export function DayCloseMobileBlocked({ labels }: { labels: DayCloseLabels }) {
  return (
    <div className="p-8 text-center space-y-3">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-biz-warning-subtle">
        <XCircle weight="fill" className="w-10 h-10 text-biz-warning" />
      </div>
      <p className="text-sm font-semibold text-biz-foreground">{labels.errors.mobileBlocked}</p>
    </div>
  );
}
